import { createContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

export interface ConnectedAccount {
  id: string;
  platform: string;
  platform_user_id: string;
  platform_handle: string | null;
  connected_at: string;
  last_synced_at: string | null;
  is_active: boolean;
  metadata?: Record<string, any>;
  token_expires_at?: string | null;
}

interface AccountFilterContextType {
  selectedAccountIds: string[];
  setSelectedAccountIds: (ids: string[]) => void;
  connectedAccounts: ConnectedAccount[];
  isLoading: boolean;
  refreshAccounts: () => Promise<void>;
}

export const AccountFilterContext = createContext<AccountFilterContextType | undefined>(undefined);

interface AccountFilterProviderProps {
  children: ReactNode;
}

export const AccountFilterProvider = ({ children }: AccountFilterProviderProps) => {
  const { profile } = useAuth();
  const [selectedAccountIds, setSelectedAccountIdsState] = useState<string[]>([]);
  const [connectedAccounts, setConnectedAccounts] = useState<ConnectedAccount[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const getStorageKey = () => profile?.id ? `accountFilter_${profile.id}` : null;

  const loadFromStorage = (accounts: ConnectedAccount[]) => {
    const storageKey = getStorageKey();
    if (!storageKey) return [];

    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        const parsedIds = JSON.parse(stored) as string[];
        // Validate stored IDs against current accounts
        const validIds = parsedIds.filter(id => 
          accounts.some(account => account.id === id)
        );
        return validIds.length > 0 ? validIds : accounts.map(a => a.id);
      }
    } catch (error) {
      console.error('Failed to load account filter from storage:', error);
    }
    
    // Default: select all accounts
    return accounts.map(a => a.id);
  };

  const saveToStorage = (ids: string[]) => {
    const storageKey = getStorageKey();
    if (!storageKey) return;

    try {
      localStorage.setItem(storageKey, JSON.stringify(ids));
    } catch (error) {
      console.error('Failed to save account filter to storage:', error);
    }
  };

  const fetchAccounts = useCallback(async () => {
    if (!profile?.id) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('connected_accounts')
        .select('id, platform, platform_user_id, platform_handle, connected_at, last_synced_at, is_active, metadata, token_expires_at')
        .eq('profile_id', profile.id)
        .eq('is_active', true)
        .order('platform')
        .order('connected_at');

      if (error) throw error;

      const accounts = (data || []) as ConnectedAccount[];
      setConnectedAccounts(accounts);

      // Load selection from storage or default to all
      const initialSelection = loadFromStorage(accounts);
      setSelectedAccountIdsState(initialSelection);
    } catch (error) {
      console.error('Failed to fetch connected accounts:', error);
      setConnectedAccounts([]);
      setSelectedAccountIdsState([]);
    } finally {
      setIsLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.id]);

  const refreshAccounts = useCallback(async () => {
    await fetchAccounts();
  }, [fetchAccounts]);

  const setSelectedAccountIds = useCallback((ids: string[]) => {
    setSelectedAccountIdsState(ids);
    saveToStorage(ids);
  }, [profile?.id]);

  // Fetch accounts on mount or when profile changes
  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);

  const value: AccountFilterContextType = {
    selectedAccountIds,
    setSelectedAccountIds,
    connectedAccounts,
    isLoading,
    refreshAccounts,
  };

  return (
    <AccountFilterContext.Provider value={value}>
      {children}
    </AccountFilterContext.Provider>
  );
};
